import { useDevStore } from "@gik/core/store/DevStore";
import React from "react";
import ReactDOM from "react-dom";

const UNBOUNCE_BAR_HEIGHT = 52;

/**
 * Dev-only fake Unbounce promo banner (toggled from DevTools) — GIK-11069.
 *
 * Lets us check the Corp top nav bar alongside a promo banner, since both push
 * the fixed Give InKind header down. It reproduces the parts of the real
 * Unbounce banner that matter for layout:
 *
 * - a `.ub-emb-container > div > div` structure portaled to `<body>` (exactly
 *   where the real embed lives), which {@link useHasUnbounceBar} detects and
 *   which then adds the header's `--unbounce` offset, and
 * - a `position: fixed` bar at the top plus a `margin-top` on `<body>`, exactly
 *   as Unbounce injects (`html body { margin-top: 52px !important }`).
 *
 * Renders nothing in production paths — it only appears when the dev flag is on.
 */
export function TestUnbounceBar(): React.ReactElement | null {
	const show = useDevStore((store) => store.showTestUnbounceBar);
	const [isMounted, setIsMounted] = React.useState(false);
	React.useEffect(() => setIsMounted(true), []);

	// Mirror Unbounce pushing the whole document down to make room for the bar.
	React.useEffect(() => {
		if (!isMounted || !show) return;
		const previous = document.body.style.marginTop;
		document.body.style.marginTop = `${UNBOUNCE_BAR_HEIGHT}px`;
		return () => {
			document.body.style.marginTop = previous;
		};
	}, [isMounted, show]);

	if (!isMounted || !show) return null;

	// Portal to <body> so the container is a direct body child, like the real
	// embed — this is what useHasUnbounceBar observes.
	return ReactDOM.createPortal(
		<div className="ub-emb-container">
			<div>
				<div className="ub-emb-bar ub-emb-visible">
					<div className="gik-test-unbounce-bar">
						Test Unbounce Banner (dev only)
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
}
